import { useQuery } from "@apollo/client";
import { useMemo, useState } from "react";
import {
  useQueryParam,
  StringParam,
  NumberParam,
  withDefault
} from "use-query-params";

import { Container } from "@material-ui/core";

import ErrorCard from "src/components/ErrorCard";
import LoadingIndicator from "src/components/LoadingIndicator";
import PageTitle from "src/components/PageTitle";
import { useSubaccount } from "src/components/SubaccountProvider";
import { VanIntegrationFields } from "src/graphql/generated/VanIntegrationFields";
import {
  addVanIntegration,
  addVanIntegrationVariables
} from "src/graphql/generated/addVanIntegration";
import {
  deleteVanIntegration,
  deleteVanIntegrationVariables
} from "src/graphql/generated/deleteVanIntegration";
import {
  editVanIntegration,
  editVanIntegrationVariables
} from "src/graphql/generated/editVanIntegration";
import {
  listVanIntegrations,
  listVanIntegrations_listVanIntegrations
} from "src/graphql/generated/listVanIntegrations";
import {
  undeleteVanIntegration,
  undeleteVanIntegrationVariables
} from "src/graphql/generated/undeleteVanIntegration";
import {
  ADD_VAN_INTEGRATION,
  DELETE_VAN_INTEGRATION,
  EDIT_VAN_INTEGRATION,
  LIST_VAN_INTEGRATIONS,
  UNDELETE_VAN_INTEGRATION
} from "src/graphql/integrations";
import useMutation from "src/util/useMutation";

import AddIntegrationDialog, {
  AddIntegrationValues
} from "./AddIntegrationDialog";
import EditIntegrationDialog from "./EditIntegrationDialog";
import IntegrationListControls from "./IntegrationListControls";
import IntegrationTable from "./IntegrationTable";
import ViewIntegrationDialog from "./ViewIntegrationDialog";
import {
  IntegrationSort,
  IntegrationStatus,
  INTEGRATION_SORTS,
  INTEGRATION_STATUSES
} from "./constants";

function validateSort(sort: string): IntegrationSort {
  return (
    INTEGRATION_SORTS.find(s => s.key === sort)?.key ?? INTEGRATION_SORTS[0].key
  );
}

function validateStatus(status: string): IntegrationStatus {
  return (
    INTEGRATION_STATUSES.find(s => s.key === status)?.key ??
    INTEGRATION_STATUSES[0].key
  );
}

type Integration = listVanIntegrations_listVanIntegrations;

export default function Integrations(): JSX.Element {
  const { subaccount } = useSubaccount()!;
  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );
  const [status, setStatus] = useQueryParam(
    "status",
    withDefault(StringParam, INTEGRATION_STATUSES[0].key)
  );
  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(StringParam, INTEGRATION_SORTS[0].key)
  );

  const validatedStatus = validateStatus(status);
  const validatedSort = validateSort(sort);

  const { loading, error, data, refetch } = useQuery<listVanIntegrations>(
    LIST_VAN_INTEGRATIONS,
    {
      variables: {
        includeDeleted: true,
        subaccountId: subaccount.id
      }
    }
  );

  const [addIntegrationDialogOpen, toggleAddIntegrationDialog] =
    useState(false);

  const [addIntegration] = useMutation<
    addVanIntegration,
    addVanIntegrationVariables
  >(ADD_VAN_INTEGRATION);

  const [editingIntegration, setEditingIntegration] =
    useState<VanIntegrationFields | null>(null);

  const [viewingIntegration, setViewingIntegration] =
    useState<VanIntegrationFields | null>(null);

  const [editIntegration] = useMutation<
    editVanIntegration,
    editVanIntegrationVariables
  >(EDIT_VAN_INTEGRATION);

  const [deleteIntegration] = useMutation<
    deleteVanIntegration,
    deleteVanIntegrationVariables
  >(DELETE_VAN_INTEGRATION, {
    onErrorSnackbarMessage: "Error archiving integration"
  });

  const [undeleteIntegration] = useMutation<
    undeleteVanIntegration,
    undeleteVanIntegrationVariables
  >(UNDELETE_VAN_INTEGRATION, {
    onErrorSnackbarMessage: "Error unarchiving integration"
  });

  const filteredIntegrations: Integration[] = useMemo(() => {
    const allIntegrations = data?.listVanIntegrations ?? [];
    const filtered = allIntegrations.filter(integration => {
      // status filter
      switch (validatedStatus) {
        case "active":
          return !integration.deletedAt;
        case "archived":
          return integration.deletedAt;
      }
    });

    // sort
    return filtered.sort((a, b) => {
      switch (validatedSort) {
        case "created_at_asc":
          return a.createdAt - b.createdAt;
        case "created_at_desc":
          return b.createdAt - a.createdAt;
        case "name_asc":
          return (a.name || "").localeCompare(b.name || "");
        case "name_desc":
          return (b.name || "").localeCompare(a.name || "");
      }
    });
  }, [data, validatedStatus, validatedSort]);

  const rows = filteredIntegrations.slice(
    page * pageSize,
    (page + 1) * pageSize
  );

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  async function handleDelete(integrationId: string) {
    await deleteIntegration({ variables: { id: integrationId } });
    await refetch();
  }

  async function handleUndelete(integrationid: string) {
    await undeleteIntegration({ variables: { id: integrationid } });
    await refetch();
  }

  async function handleAddIntegration(values: AddIntegrationValues) {
    const { errors } = await addIntegration({
      variables: { ...values, subaccountId: subaccount.id }
    });
    if (errors) {
      throw errors;
    } else {
      await refetch();
      toggleAddIntegrationDialog(false);
    }
  }

  async function handleEditIntegration(values: {
    name: string;
    password: string;
  }) {
    if (!editingIntegration) {
      return;
    }
    const { errors } = await editIntegration({
      variables: { id: editingIntegration.id, ...values }
    });
    if (errors) {
      throw errors;
    } else {
      await refetch();
      setEditingIntegration(null);
    }
  }

  return (
    <Container>
      <PageTitle title="Integrations" />
      <IntegrationListControls
        onClickAdd={() => toggleAddIntegrationDialog(true)}
        statusFilter={validatedStatus}
        setStatusFilter={setStatus}
        sort={validatedSort}
        setSort={setSort}
      />
      {loading ? (
        <LoadingIndicator />
      ) : (
        <IntegrationTable
          rows={rows}
          handleChangePage={setPage}
          handleChangeRowsPerPage={newRowsPerPage => {
            setPageSize(newRowsPerPage);
            setPage(0);
          }}
          rowsPerPage={pageSize}
          page={page}
          totalCount={filteredIntegrations.length}
          handleDelete={handleDelete}
          handleUndelete={handleUndelete}
          handleEdit={setEditingIntegration}
          handleView={setViewingIntegration}
        />
      )}
      <AddIntegrationDialog
        open={addIntegrationDialogOpen}
        onClose={() => toggleAddIntegrationDialog(false)}
        onSubmit={handleAddIntegration}
      />
      {editingIntegration ? (
        <EditIntegrationDialog
          open
          onClose={() => setEditingIntegration(null)}
          integration={editingIntegration}
          onSubmit={handleEditIntegration}
        />
      ) : null}
      {viewingIntegration ? (
        <ViewIntegrationDialog
          open
          onClose={() => setViewingIntegration(null)}
          integration={viewingIntegration}
        />
      ) : null}
    </Container>
  );
}
